import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  CheckCheck,
  Volume2,
  VolumeX,
  FileText,
  MessageSquare,
  RefreshCw,
  Sparkles,
  ExternalLink,
  Trash2,
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNotifications } from '@/context/NotificationContext';
import { subscribeUserToPush, getPushPermission } from '@/lib/pushSubscription';

function formatRelativeTime(dateString) {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 45) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    return `${diffDay}d ago`;
  } catch {
    return '';
  }
}

function getNotificationIcon(type) {
  switch (type) {
    case 'prescription.assigned':
      return <FileText className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />;
    case 'prescription.note_added':
      return <MessageSquare className="w-4 h-4 text-teal-600 dark:text-teal-400" />;
    case 'prescription.status_changed':
      return <RefreshCw className="w-4 h-4 text-amber-600 dark:text-amber-400" />;
    default:
      return <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />;
  }
}

export function NotificationDropdown() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [pushSubscribing, setPushSubscribing] = useState(false);
  const [pushStatus, setPushStatus] = useState(() => getPushPermission());

  const {
    notifications,
    unreadCount,
    isRealtimeConnected,
    audioMuted,
    toggleAudio,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
  } = useNotifications();

  const handleNotificationClick = async (notif) => {
    if (!notif.is_read) {
      await markAsRead(notif.id);
    }
    setOpen(false);
    if (notif.action_url) {
      navigate(notif.action_url);
    }
  };

  const handleEnablePush = async () => {
    setPushSubscribing(true);
    try {
      const res = await subscribeUserToPush();
      setPushStatus(getPushPermission());
      if (!res.success && res.reason) {
        console.info('[Push]', res.reason);
      }
    } finally {
      setPushSubscribing(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Notifications (${unreadCount} unread)`}
          className="relative inline-flex items-center justify-center w-9 h-9 rounded-full text-stone-600 dark:text-stone-300 hover:text-emerald-700 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
        >
          <Bell className="w-5 h-5" />

          {/* Unread badge with subtle pulse */}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-emerald-600 px-1 text-[10px] font-semibold text-white shadow-sm ring-2 ring-white dark:ring-stone-900 animate-pulse whitespace-nowrap">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}

          {/* Realtime connection indicator dot anchored directly to bell bottom-right */}
          <span
            title={isRealtimeConnected ? 'Realtime live stream connected' : 'Connecting realtime...'}
            className={`absolute bottom-1 right-1 w-2 h-2 rounded-full ring-1.5 ring-white dark:ring-stone-900 ${
              isRealtimeConnected ? 'bg-emerald-500' : 'bg-amber-400'
            }`}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        collisionPadding={12}
        className="w-[calc(100vw-1.5rem)] sm:w-[420px] max-w-[420px] p-0 shadow-2xl border border-stone-200/90 dark:border-stone-800 bg-white dark:bg-stone-900 rounded-2xl overflow-hidden z-50"
      >
        {/* Header */}
        <div className="px-3.5 py-3 border-b border-stone-100 dark:border-stone-800 bg-stone-50/90 dark:bg-stone-900/90 backdrop-blur-sm">
          <div className="flex items-center justify-between gap-2">
            {/* Title & Status Badges */}
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="font-semibold text-sm text-stone-900 dark:text-stone-100 truncate">
                Notifications
              </h3>
              {unreadCount > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300 whitespace-nowrap flex-shrink-0">
                  {unreadCount} new
                </span>
              )}
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap flex-shrink-0 ${
                  isRealtimeConnected
                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
                    : 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
                }`}
                title={isRealtimeConnected ? 'Live real-time stream connected' : 'Connecting to live updates'}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${isRealtimeConnected ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
                <span className="hidden xs:inline">{isRealtimeConnected ? 'Live' : 'Syncing'}</span>
              </span>
            </div>

            {/* Actions Bar */}
            <div className="flex items-center gap-1 shrink-0">
              {/* Audio chime toggle */}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-lg text-stone-500 hover:text-stone-900 hover:bg-stone-100 dark:hover:text-stone-100 dark:hover:bg-stone-800"
                onClick={toggleAudio}
                title={audioMuted ? 'Unmute chime' : 'Mute chime'}
                aria-label={audioMuted ? 'Unmute chime' : 'Mute chime'}
              >
                {audioMuted ? (
                  <VolumeX className="w-3.5 h-3.5 text-stone-400" />
                ) : (
                  <Volume2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                )}
              </Button>

              {/* Mark all as read */}
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs font-medium text-stone-600 hover:text-emerald-700 hover:bg-emerald-50 dark:text-stone-300 dark:hover:text-emerald-400 dark:hover:bg-emerald-950/40 rounded-lg transition-colors"
                  onClick={markAllAsRead}
                  title="Mark all notifications as read"
                >
                  <CheckCheck className="w-3.5 h-3.5 sm:mr-1 text-emerald-600 dark:text-emerald-400" />
                  <span className="hidden sm:inline">Read all</span>
                </Button>
              )}

              {/* Clear all notifications trigger */}
              {notifications.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs font-medium text-stone-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:text-rose-400 dark:hover:bg-rose-950/40 rounded-lg transition-colors"
                  onClick={() => setConfirmClearOpen(true)}
                  title="Clear all notifications"
                >
                  <Trash2 className="w-3.5 h-3.5 sm:mr-1" />
                  <span className="hidden sm:inline">Clear all</span>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Dedicated Clear All Confirmation Banner */}
        {confirmClearOpen && (
          <div className="px-3.5 py-2 bg-rose-50/95 dark:bg-rose-950/60 border-b border-rose-200 dark:border-rose-900/60 flex items-center justify-between gap-2 text-xs text-rose-900 dark:text-rose-200 animate-in fade-in slide-in-from-top-1">
            <span className="font-medium text-[11px] sm:text-xs truncate">
              Clear all {notifications.length} notification{notifications.length > 1 ? 's' : ''}?
            </span>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button
                size="sm"
                variant="destructive"
                className="h-6 px-2.5 text-[11px] font-medium rounded-md shadow-xs"
                onClick={async () => {
                  setConfirmClearOpen(false);
                  await clearAllNotifications();
                }}
              >
                Yes, clear all
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-6 px-2 text-[11px] bg-white dark:bg-stone-900 border-stone-300 dark:border-stone-700 rounded-md"
                onClick={() => setConfirmClearOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Notifications List */}
        <ScrollArea className="max-h-[360px] sm:max-h-[400px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-2.5 shadow-xs">
                <Bell className="w-6 h-6 stroke-[1.5]" />
              </div>
              <p className="text-sm font-semibold text-stone-800 dark:text-stone-200">
                All caught up!
              </p>
              <p className="text-xs text-stone-400 dark:text-stone-500 mt-1 max-w-[260px] leading-relaxed">
                New prescription assignments and status updates will appear here automatically.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-stone-100 dark:divide-stone-800/60">
              {notifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleNotificationClick(notif)}
                  className={`group relative flex items-start gap-3 p-3.5 transition-colors cursor-pointer hover:bg-stone-50/90 dark:hover:bg-stone-800/40 ${
                    !notif.is_read
                      ? 'bg-emerald-50/40 dark:bg-emerald-950/20'
                      : 'bg-transparent'
                  }`}
                >
                  {/* Icon badge */}
                  <div
                    className={`mt-0.5 flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center shadow-xs ${
                      notif.type === 'prescription.assigned'
                        ? 'bg-emerald-100/80 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400'
                        : notif.type === 'prescription.status_changed'
                        ? 'bg-amber-100/80 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400'
                        : 'bg-teal-100/80 text-teal-700 dark:bg-teal-950/60 dark:text-teal-400'
                    }`}
                  >
                    {getNotificationIcon(notif.type)}
                  </div>

                  {/* Body */}
                  <div className="flex-1 min-w-0 pr-1">
                    <div className="flex items-baseline justify-between gap-1.5">
                      <p
                        className={`text-xs truncate ${
                          !notif.is_read
                            ? 'text-stone-900 dark:text-stone-100 font-semibold'
                            : 'text-stone-700 dark:text-stone-300 font-medium'
                        }`}
                      >
                        {notif.title || 'AyurClinic Alert'}
                      </p>
                      <span className="text-[10px] text-stone-400 dark:text-stone-500 shrink-0 tabular-nums">
                        {formatRelativeTime(notif.created_at)}
                      </span>
                    </div>

                    <p className="text-xs text-stone-600 dark:text-stone-400 line-clamp-2 mt-0.5 leading-relaxed">
                      {notif.message}
                    </p>
                  </div>

                  {/* Actions & Unread indicator */}
                  <div className="flex items-center gap-1.5 shrink-0 self-center pl-1">
                    {!notif.is_read && (
                      <span
                        className="w-2 h-2 rounded-full bg-emerald-500 shrink-0 ring-2 ring-emerald-100 dark:ring-emerald-950"
                        title="Unread notification"
                      />
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notif.id);
                      }}
                      className="p-1 rounded-md text-stone-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 dark:hover:text-rose-400 transition-colors opacity-70 sm:opacity-0 sm:group-hover:opacity-100"
                      title="Delete notification"
                      aria-label="Delete notification"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer / Web Push Enablement */}
        {pushStatus === 'default' && (
          <div className="px-4 py-2.5 bg-emerald-50/60 dark:bg-emerald-950/30 border-t border-emerald-100 dark:border-emerald-900/50 flex items-center justify-between gap-2">
            <span className="text-xs text-emerald-900 dark:text-emerald-200">
              Enable background alerts when tab is dormant?
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={pushSubscribing}
              onClick={handleEnablePush}
              className="h-7 text-xs border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-100 dark:hover:bg-emerald-900/50"
            >
              {pushSubscribing ? 'Enabling...' : 'Enable'}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default NotificationDropdown;
